import { AlertTriangle, Calendar, Coins, Copy, DollarSign, Globe, MapPin, Phone, User } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { parsePhoneNumberFromString } from 'libphonenumber-js/min';
import Layout from '../components/Layout';
import { countryAPI, intentAPI, kycAPI, limitsAPI, userAPI } from '../services/api';
import { responseData } from '../services/apiResponse';
import { trackEvent } from '../services/analytics';

const STABLECOIN_META = {
  USDC: { symbol: '$', logoPath: '/assets/tokens/usdc.png' },
  USDT: { symbol: '₮', logoPath: '/assets/tokens/usdt.png' },
};

const MIN_PAYMENT_AMOUNT_USD = 30;

const DEFAULT_PURPOSE_CODE = 'family_support';

const getCountryValue = (country) => country?.alpha2 || country?.alpha3 || '';
const getCurrencyCode = (country) => (country?.currency_code || '').trim().toUpperCase();
const checkoutSelectionStorageKey = (requestId) => `checkout_selection:${requestId}`;

const findCountryByCode = (countries, code) => {
  const normalizedCode = String(code || '').trim().toUpperCase();
  if (!normalizedCode) return undefined;

  return countries.find((country) => (
    String(country?.alpha2 || '').trim().toUpperCase() === normalizedCode ||
    String(country?.alpha3 || '').trim().toUpperCase() === normalizedCode
  ));
};

const getKycCountryCode = (kycInfo) => (
  kycInfo?.country_code_alpha2 ||
  kycInfo?.country_code_alpha3 ||
  kycInfo?.country_code ||
  ''
);

const getPhoneCountryCode = (phone) => {
  const normalizedPhone = String(phone || '').trim();
  if (!normalizedPhone) return '';
  try {
    return parsePhoneNumberFromString(normalizedPhone)?.country || '';
  } catch {
    return '';
  }
};

export const getAvailableCurrencies = (countries) => (
  [...new Set(countries.map(getCurrencyCode).filter(Boolean))].sort()
);

export function resolveCheckoutSelection(
  countries,
  requestId,
  storage = localStorage,
  kycInfo = null,
  senderPhone = '',
  currentUserPhone = '',
) {
  let storedSelection = null;
  try {
    storedSelection = JSON.parse(storage.getItem(checkoutSelectionStorageKey(requestId)) || 'null');
  } catch {
    storage.removeItem(checkoutSelectionStorageKey(requestId));
  }

  const storedCountry = (storedSelection?.countryAlpha2 || '').trim().toUpperCase();
  const defaultCountry = findCountryByCode(countries, storedCountry) ||
    findCountryByCode(countries, getKycCountryCode(kycInfo)) ||
    findCountryByCode(countries, getPhoneCountryCode(senderPhone)) ||
    findCountryByCode(countries, getPhoneCountryCode(currentUserPhone)) ||
    countries.find((country) => country.alpha2 === 'US' || country.alpha3 === 'USA') ||
    countries[0];
  if (!defaultCountry) return null;

  const storedCurrency = (storedSelection?.fiatCurrency || '').trim().toUpperCase();
  const restoreManualCurrency =
    storedSelection?.fiatCurrencyManuallySelected === true &&
    getAvailableCurrencies(countries).includes(storedCurrency);

  return {
    country: defaultCountry,
    fiatCurrency: restoreManualCurrency ? storedCurrency : getCurrencyCode(defaultCountry),
    fiatCurrencyManuallySelected: restoreManualCurrency,
  };
}

const formatDOB = (dob) => {
  if (!dob) return dob;
  const s = dob.replace(/-/g, '');
  if (s.length !== 8) return dob;
  const year = parseInt(s.slice(0, 4), 10);
  const month = parseInt(s.slice(4, 6), 10) - 1;
  const day = parseInt(s.slice(6, 8), 10);
  const date = new Date(year, month, day);
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const TransactionReview = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [intent, setIntent] = useState(null);
  const [amount, setAmount] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [fiatCurrency, setFiatCurrency] = useState('');
  const [fiatCurrencyManuallySelected, setFiatCurrencyManuallySelected] = useState(false);
  const [countries, setCountries] = useState([]);
  const [kycInfo, setKycInfo] = useState(null);
  const [senderPhone, setSenderPhone] = useState('');
  const [currentUserPhone, setCurrentUserPhone] = useState('');
  const [transferLimits, setTransferLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [userLimits, setUserLimits] = useState(null);
  const [limitWarning, setLimitWarning] = useState(null);
  const purposeCode = intent?.reason_for_sending?.trim() || DEFAULT_PURPOSE_CODE;
  const currencies = getAvailableCurrencies(countries);

  const loadIntent = useCallback(async () => {
    try {
      setLoading(true);
      const response = await intentAPI.getIntent(requestId);
      const intentData = responseData(response);
      setIntent(intentData);
      setSenderPhone(intentData?.sender_phone || '');
      setAmount(intentData.requested_amount_usd.toString());
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load intent');
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  const loadCountries = useCallback(async () => {
    try {
      const [response, kycResponse] = await Promise.all([
        countryAPI.getCountries(),
        kycAPI.getKycInfo().catch((err) => {
          console.error('Failed to load KYC info:', err);
          return null;
        }),
      ]);
      const availableCountries = responseData(response) || [];
      setCountries(availableCountries);
      setKycInfo(responseData(kycResponse)?.kyc_info || null);
    } catch (err) {
      console.error('Failed to load countries:', err);
    }
  }, []);

  useEffect(() => {
    loadIntent();
  }, [loadIntent]);

  useEffect(() => {
    loadUserLimits();
    loadCountries();
    loadTransferLimits();
  }, [loadCountries]);

  useEffect(() => {
    if (countries.length === 0) return;
    const selection = resolveCheckoutSelection(
      countries,
      requestId,
      localStorage,
      kycInfo,
      senderPhone,
      currentUserPhone,
    );
    if (selection) {
      setSelectedCountry(getCountryValue(selection.country));
      setFiatCurrency(selection.fiatCurrency);
      setFiatCurrencyManuallySelected(selection.fiatCurrencyManuallySelected);
    }
  }, [countries, currentUserPhone, kycInfo, requestId, senderPhone]);

  const loadUserLimits = async () => {
    try {
      const userId = localStorage.getItem('user_id');
      if (!userId) return;

      const response = await userAPI.getCurrentUser(userId);
      const user = responseData(response);
      setUserLimits(user);
      setCurrentUserPhone(user?.phone || '');
    } catch (err) {
      console.error('Failed to load user limits:', err);
    }
  };

  const loadTransferLimits = async () => {
    try {
      const response = await limitsAPI.getLimits();
      setTransferLimits(responseData(response) || null);
    } catch (err) {
      console.error('Failed to load transfer limits:', err);
    }
  };

  const checkLimits = () => {
    if (!amount) return null;

    const transactionAmount = parseFloat(amount);

    // Check global transfer limits first
    if (transferLimits) {
      const perTransactionLimit = parseFloat(transferLimits.per_transaction) || 999;
      const perMonthLimit = parseFloat(transferLimits.per_month) || 2999;
      const perYearLimit = parseFloat(transferLimits.per_year) || 9999;

      // Check per transaction limit
      if (transactionAmount > perTransactionLimit) {
        return {
          type: 'per_transaction',
          message: `This transaction exceeds the maximum per transaction limit of $${perTransactionLimit.toFixed(2)}`,
          limit: perTransactionLimit
        };
      }

      // Check per month limit (using user's spent amount)
      if (userLimits) {
        const monthlySpent = userLimits.monthly_spent_usd || 0;
        const monthlyRemaining = perMonthLimit - monthlySpent;
        if (transactionAmount > monthlyRemaining) {
          return {
            type: 'per_month',
            message: `This transaction exceeds your monthly limit. Remaining: $${monthlyRemaining.toFixed(2)} of $${perMonthLimit.toFixed(2)}`,
            remaining: monthlyRemaining,
            limit: perMonthLimit
          };
        }
      }

      // Check per year limit (using user's spent amount - we'll approximate with yearly)
      if (userLimits) {
        // For now, we don't have yearly spent tracking, so we just check against the limit
        // This could be enhanced with actual yearly tracking
        if (transactionAmount > perYearLimit) {
          return {
            type: 'per_year',
            message: `This transaction exceeds the maximum yearly limit of $${perYearLimit.toFixed(2)}`,
            limit: perYearLimit
          };
        }
      }
    }

    // Fallback to user-specific limits if no global limits
    if (!userLimits) return null;

    const yearlyLimit = userLimits.yearly_limit_usd || 9999;
    const monthlyLimit = userLimits.monthly_limit_usd || 3000;
    const yearlySpent = userLimits.yearly_spent_usd || 0;
    const monthlySpent = userLimits.monthly_spent_usd || 0;

    const yearlyRemaining = yearlyLimit - yearlySpent;
    const monthlyRemaining = monthlyLimit - monthlySpent;

    if (transactionAmount > yearlyRemaining) {
      return {
        type: 'yearly',
        message: `This transaction exceeds your yearly limit. Remaining: $${yearlyRemaining.toFixed(2)}`,
        remaining: yearlyRemaining,
        limit: yearlyLimit
      };
    }

    if (transactionAmount > monthlyRemaining) {
      return {
        type: 'monthly',
        message: `This transaction exceeds your monthly limit. Remaining: $${monthlyRemaining.toFixed(2)}`,
        remaining: monthlyRemaining,
        limit: monthlyLimit
      };
    }

    return null;
  };

  const handleCopyPhone = () => {
    if (intent?.receiver_phone) {
      navigator.clipboard.writeText(intent.receiver_phone);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCallPhone = () => {
    if (intent?.receiver_phone) {
      window.location.href = `tel:${intent.receiver_phone}`;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const numericAmount = parseFloat(amount);

    if (!numericAmount || numericAmount < MIN_PAYMENT_AMOUNT_USD) {
      setLimitWarning(null);
      setError(`Minimum payment amount is $${MIN_PAYMENT_AMOUNT_USD}. Please enter $${MIN_PAYMENT_AMOUNT_USD} or more.`);
      return;
    }
    
    if (!selectedCountry) {
      setError('Please select a country');
      return;
    }

    if (!fiatCurrency) {
      setError('Please select a currency');
      return;
    }

    // 检查限额
    const limitCheck = checkLimits();
    if (limitCheck) {
      setLimitWarning(limitCheck);
      setError(limitCheck.message);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const selectedCountryRecord = countries.find((country) => getCountryValue(country) === selectedCountry);
      if (!selectedCountryRecord?.alpha2 || !selectedCountryRecord?.alpha3) {
        setError('Country details are incomplete. Please reselect your country.');
        setSubmitting(false);
        return;
      }
      
      // Store data for TransakCheckout page
      localStorage.setItem('purpose_code', purposeCode);
      localStorage.setItem('confirmed_amount', numericAmount.toString());
      localStorage.setItem('selected_country', selectedCountryRecord.alpha2);
      localStorage.setItem('selected_country_alpha2', selectedCountryRecord.alpha2);
      localStorage.setItem('selected_country_alpha3', selectedCountryRecord.alpha3);
      localStorage.setItem('selected_country_name', selectedCountryRecord.name || '');
      localStorage.setItem('fiat_currency', fiatCurrency);
      localStorage.setItem('fiat_currency_manually_selected', String(fiatCurrencyManuallySelected));
      localStorage.setItem(checkoutSelectionStorageKey(requestId), JSON.stringify({
        countryAlpha2: selectedCountryRecord.alpha2,
        fiatCurrency,
        fiatCurrencyManuallySelected,
      }));

      // The checkout API enforces the configured KYC policy; its errors route users to verification.
      trackEvent('begin_checkout', { currency: fiatCurrency });
      navigate(`/onramp-checkout/${requestId}`);
    } catch (err) {
      const errorDetail = err.response?.data?.detail || 'Failed to continue to payment';
      setError(errorDetail);
      
      // 检查是否是限额错误
      if (errorDetail.includes('limit exceeded')) {
        const match = errorDetail.match(/Remaining: \$([\d.]+)/);
        if (match) {
          setLimitWarning({
            type: errorDetail.includes('Daily') ? 'daily' : 'monthly',
            message: errorDetail,
            remaining: parseFloat(match[1])
          });
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout step={3} totalSteps={5} onBack={() => navigate(-1)}>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout step={3} totalSteps={5} onBack={() => navigate(-1)}>
      <div className="space-y-6">
        <div className="text-center">
          {/* <FileText className="w-16 h-16 text-primary-600 mx-auto mb-4" /> */}
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Review Transaction</h2>
          <p className="text-gray-600">Please review and confirm the payment details</p>
        </div>

        {limitWarning && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-red-800 mb-2">Transaction Limit Exceeded</h3>
                <p className="text-red-700 text-sm mb-3">{limitWarning.message}</p>
                {userLimits && (
                  <div className="bg-white rounded-lg p-3 text-sm">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-gray-600 text-xs">Yearly Remaining</p>
                        <p className="font-semibold text-gray-800">
                          ${((userLimits.yearly_limit_usd || 9999) - (userLimits.yearly_spent_usd || 0)).toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 text-xs">Monthly Remaining</p>
                        <p className="font-semibold text-gray-800">
                          ${((userLimits.monthly_limit_usd || 3000) - (userLimits.monthly_spent_usd || 0)).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {error && !limitWarning && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex items-start p-4 bg-gray-50 rounded-lg">
            <User className="w-5 h-5 text-primary-600 mt-1 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-gray-600">Receiver</p>
              <p className="font-semibold text-gray-800 text-lg">{intent?.receiver_full_legal_name}</p>
              {intent?.receiver_wallet_address_masked ? (
                <p className="mt-1 font-mono text-sm text-gray-700">Wallet: {intent.receiver_wallet_address_masked}</p>
              ) : null}
              {intent?.receiver_phone && (
                <div className="flex items-center space-x-2 mt-2">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700 font-medium">{intent.receiver_phone}</span>
                  <button
                    type="button"
                    onClick={handleCopyPhone}
                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                    title="Copy phone number"
                  >
                    <Copy className="w-4 h-4 text-gray-600" />
                  </button>
                  <button
                    type="button"
                    onClick={handleCallPhone}
                    className="p-1 hover:bg-primary-100 rounded transition-colors"
                    title="Call phone number"
                  >
                    <Phone className="w-4 h-4 text-primary-600" />
                  </button>
                  {copied && (
                    <span className="text-xs text-green-600 font-medium">Copied!</span>
                  )}
                </div>
              )}
              {intent?.receiver_dob && (
                <div className="flex items-center space-x-2 mt-1">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <p className="text-sm text-gray-700">Date of birth: {formatDOB(intent.receiver_dob)}</p>
                </div>
              )}
              {intent?.receiver_address && (
                <div className="flex items-start space-x-2 mt-1">
                  <MapPin className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">{intent.receiver_address}</p>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <div className="inline-flex items-center gap-1.5">
                {(() => {
                  const meta = STABLECOIN_META[intent?.currency];
                  return meta ? (
                    <img src={meta.logoPath} alt={intent.currency} className="w-4 h-4 rounded-full object-contain" />
                  ) : (
                    <DollarSign className="w-4 h-4" />
                  );
                })()}
                <span>Amount (USD) *</span>
              </div>
            </label>
            <input
              type="number"
              step="0.01"
              min="25"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                if (error?.includes('Minimum payment amount')) {
                  setError(null);
                }
              }}
              required
              className="input-field text-2xl font-bold"
            />
            <p className="text-xs text-gray-500 mt-1">
              Requested: ${intent?.requested_amount_usd.toFixed(2)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Minimum amount: ${MIN_PAYMENT_AMOUNT_USD.toFixed(2)}
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Globe className="inline w-4 h-4 mr-1" />
              Sending Country *
            </label>
            <select
              value={selectedCountry}
              onChange={(e) => {
                const countryValue = e.target.value;
                const country = countries.find(item => getCountryValue(item) === countryValue);
                setSelectedCountry(countryValue);
                setFiatCurrency(getCurrencyCode(country));
                setFiatCurrencyManuallySelected(false);
              }}
              required
              className="input-field"
            >
              <option value="">Select your country...</option>
              {countries.map(country => (
                <option key={getCountryValue(country)} value={getCountryValue(country)}>
                  {country.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Required for KYC verification
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Coins className="inline w-4 h-4 mr-1" />
              Currency *
            </label>
            <select
              value={fiatCurrency}
              onChange={(e) => {
                setFiatCurrency(e.target.value);
                setFiatCurrencyManuallySelected(true);
              }}
              required
              className="input-field"
            >
              <option value="">Select your currency...</option>
              {currencies.map(currency => (
                <option key={currency} value={currency}>{currency}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Defaults to the currency of your sending country
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-800 mb-2">📋 Travel Rule Compliance</h3>
            <p className="text-sm text-gray-700">
              Your transaction information will be securely stored and shared with authorized parties 
              as required by international financial regulations.
            </p>
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full"
            >
              {submitting ? 'Processing...' : 'Continue'}
            </button>
          </div>
        </form>

        <p className="text-center text-xs text-gray-400 pt-4">
          A service by Kivi3 Technologies Ltd — Licensed by Dubai International Financial Centre.
        </p>
      </div>
    </Layout>
  );
};

export default TransactionReview;
